import { CircleCheck, LoaderCircle } from 'lucide-react'
import { useState } from 'react'
import type { ReactNode } from 'react'

import type { AppStatus } from '@/shared/api/contracts'
import { CopyButton, RelativeTime } from '@/shared/components/common'
import { Segmented } from '@/shared/components/ui'
import { useT } from '@/shared/i18n'

import { useSupportAppStatus } from '../api'
import { BACKENDS, envSnippet, routeSnippet, widgetSnippet, type Backend } from '../snippets'

/** Cada cuánto se pregunta si la app ya pidió su primer pase. */
const POLL_MS = 5_000

export interface ActivationGuideProps {
  app: AppStatus
}

/**
 * El tour de activación: los cuatro pasos para que aparezca el chat en la app.
 *
 * El último no se tilda a mano: el PM le pregunta a siete-soporte si la app ya
 * pidió un pase con su clave, y cuando pasa dice "Conectada" solo. Es la única
 * prueba de que la clave, la ruta y los dominios están bien a la vez.
 */
export function ActivationGuide({ app }: ActivationGuideProps) {
  const t = useT()
  const estado = useSupportAppStatus(app.id, POLL_MS)
  const actual = estado.data ?? app
  const [backend, setBackend] = useState<Backend>('fastapi')

  return (
    <ol
      aria-label={t('support.apps.tour.title', { app: app.name })}
      className="flex flex-col gap-4"
    >
      <Paso numero={1} titulo={t('support.apps.tour.env.title')}>
        <p>{t('support.apps.tour.env.description')}</p>
        <Codigo valor={envSnippet('ssk_…')} etiqueta={t('support.apps.tour.copy')} />
      </Paso>

      <Paso numero={2} titulo={t('support.apps.tour.route.title')}>
        <p>{t('support.apps.tour.route.description')}</p>
        <Segmented
          label={t('support.apps.tour.route.backend')}
          value={backend}
          options={BACKENDS}
          onChange={setBackend}
        />
        <Codigo valor={routeSnippet(backend)} etiqueta={t('support.apps.tour.copy')} />
      </Paso>

      <Paso numero={3} titulo={t('support.apps.tour.widget.title')}>
        <p>{t('support.apps.tour.widget.description')}</p>
        <Codigo valor={widgetSnippet(app.tool_slug)} etiqueta={t('support.apps.tour.copy')} />
        {actual.origins.length > 0 ? (
          <p>
            {t('support.apps.tour.widget.origins')}{' '}
            <span className="num text-text-1">{actual.origins.join(' · ')}</span>
          </p>
        ) : null}
      </Paso>

      <Paso numero={4} titulo={t('support.apps.tour.check.title')}>
        <p>{t('support.apps.tour.check.description', { app: app.name })}</p>
        <div role="status" className="flex items-center gap-2 text-sm">
          {actual.conectada ? (
            <>
              <CircleCheck aria-hidden="true" size={16} className="text-phase-done" />
              <span className="text-text-1">{t('support.apps.status.connected')}</span>
              {actual.last_seen_at ? (
                <RelativeTime value={actual.last_seen_at} className="text-2xs text-text-3" />
              ) : null}
            </>
          ) : (
            <>
              <LoaderCircle aria-hidden="true" size={16} className="animate-spin text-text-3" />
              <span className="text-text-2">{t('support.apps.tour.check.waiting')}</span>
            </>
          )}
        </div>
      </Paso>
    </ol>
  )
}

function Paso({
  numero,
  titulo,
  children,
}: {
  numero: number
  titulo: string
  children: ReactNode
}) {
  return (
    <li className="flex gap-3">
      <span
        aria-hidden="true"
        className="num flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-surface-2 text-2xs text-text-2"
      >
        {numero}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-2 text-xs text-text-3">
        <h3 className="text-sm text-text-1">{titulo}</h3>
        {children}
      </div>
    </li>
  )
}

function Codigo({ valor, etiqueta }: { valor: string; etiqueta: string }) {
  return (
    <div className="relative">
      <pre className="num overflow-x-auto rounded-[var(--radius-control)] bg-bg p-2 pr-8 text-2xs text-text-2">
        {valor}
      </pre>
      <CopyButton value={valor} label={etiqueta} className="absolute top-1 right-1" />
    </div>
  )
}
